/* import __COLOCATED_TEMPLATE__ from './verification-method.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { task } from 'ember-concurrency';
import { showNextModal, closeModal } from 'sh-www/utils/modal-controls';
import { handleErrorResponse } from 'sh-www/utils/handles-errors';
import {
  PROJECTDIGEST_VERIFICATION_METHODS,
  VALID_ATTRIBUTION_SERVICE_IDS,
} from 'sh-www/models/projectdigest-verification';
import clsx from 'clsx';

const CLASS_NAME = 'verification-method-modal';
const FOOTER_CLASS_NAME = `${CLASS_NAME}__footer`;
const FOOTER_SPACE_BETWEEN_CLASS_NAME = `${FOOTER_CLASS_NAME}--space-between`;
const FOOTER_BUTTON_GROUP_RIGHT_CLASS_NAME = `${FOOTER_CLASS_NAME}__right-side-group`;
const FOOTER_BUTTON_GROUP_RIGHT_SPREAD_CLASS_NAME = `${FOOTER_BUTTON_GROUP_RIGHT_CLASS_NAME}--spread`

const FORM_INPUTS = [
  'link',
  'minutes',
  'seconds',
  'generic-exception',
]

class ProjectVerificationMethodModal extends Component {
  @service('state-managers.application') applicationStateManager;
  @service('commands.projectdigest-commands') projectdigestCommandService;
  @service('commands.user-commands') userCommandService;
  @service shData;

  @tracked verificationMethod = null;
  @tracked imdbLink = null;
  @tracked timestampMinutes = null;
  @tracked timestampSeconds = null;
  @tracked errors = {};

  get baseClassName() {
    return CLASS_NAME;
  }

  get footerClasses() {
    return clsx(
      FOOTER_CLASS_NAME,
      this.args.model.isEditing && FOOTER_SPACE_BETWEEN_CLASS_NAME,
    );
  }

  get footerButtonGroupClasses() {
    return clsx(
      FOOTER_BUTTON_GROUP_RIGHT_CLASS_NAME,
      !this.args.model.isEditing && FOOTER_BUTTON_GROUP_RIGHT_SPREAD_CLASS_NAME,
    );
  }

  get isSelectDisabled() {
    return this.verificationSelectedTask.isRunning
      || !this.verificationMethod 
      || (this.verificationMethod === PROJECTDIGEST_VERIFICATION_METHODS.IMDB && !this.imdbLink)
      || (this.verificationMethod === PROJECTDIGEST_VERIFICATION_METHODS.TIMESTAMP && !this.timestampMinutes && !this.timestampSeconds);
  }

  get showAttributionOption() {
    const { services } = this.args.model.projectDigest;
    return services.toArray().some(({ id }) => VALID_ATTRIBUTION_SERVICE_IDS.includes(id));
  }

  @action
  setVerificationMethod(method) {
    this.errors = {};
    this.verificationMethod = method;
    this.imdbLink = null;
    this.timestampMinutes = null;
    this.timestampSeconds = null;
  }

  @action
  setIMDBLink(value) {
    this.imdbLink = value;
  }

  @action
  setTimestampMinutes(value) {
    this.timestampMinutes = value;
  }

  @action
  setTimestampSeconds(value) {
    this.timestampSeconds = value;
  }

  @action
  onSkipClicked() {
    closeModal(this);
  }

  @task
  *verificationSelectedTask() {
    this.errors = {};
    const { projectDigest, isEditing } = this.args.model;

    if (this.verificationMethod === PROJECTDIGEST_VERIFICATION_METHODS.REFERRAL) {
      showNextModal(this, 'work-samples/client', { projectDigest, isEditing });
      return;
    }

    try {
      const data = { method: this.verificationMethod };
      switch (this.verificationMethod) {
        case PROJECTDIGEST_VERIFICATION_METHODS.IMDB:
          yield this.projectdigestCommandService.createVerification(projectDigest, { ...data, link: this.imdbLink, });
          break;
        case PROJECTDIGEST_VERIFICATION_METHODS.TIMESTAMP:
          yield this.projectdigestCommandService.createVerification(projectDigest, { ...data, minutes: this.timestampMinutes, seconds: this.timestampSeconds });
          break;
        case PROJECTDIGEST_VERIFICATION_METHODS.BYLINE_CREDIT:
          yield this.projectdigestCommandService.createVerification(projectDigest, data);
          break;
        default:
          this.applicationStateManager.addToast('Selected work sample verification method not valid.');
          closeModal(this);
          return;
      }
      const response = yield this.projectdigestCommandService.getProjectDigest(projectDigest.id, ['projectdigest-verifications:verified_or_latest']);
      this.shData.pushResponseToStore(response);
      this.applicationStateManager.addToast('Work sample verification information successfully added.');
      closeModal(this);
    } catch(error) {
      handleErrorResponse(this, error, FORM_INPUTS, false);
    }
  }
}

export default ProjectVerificationMethodModal;
